import { useRef, useState } from "react";
import { useLang } from "../../lib/LangContext";
import { SectionHeader } from "./SectionHeader";
import { localImg } from "../../lib/images";

// Before/After image sets — uploaded PUREGLOSS photos.
const BA_SIZES = "(min-width: 1024px) 1100px, 100vw";
export const SETS = {
  cars: {
    before: localImg("ba-car-before", BA_SIZES),
    after: localImg("ba-car-after", BA_SIZES),
  },
  sofas: {
    before: localImg("ba-sofa-before", BA_SIZES),
    after: localImg("ba-sofa-after", BA_SIZES),
  },
};

export const Slider = ({ before, after, beforeLabel, afterLabel }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative aspect-[16/10] w-full overflow-hidden bg-black select-none cursor-ew-resize touch-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      {/* AFTER (right base) — full width, contained to preserve aspect, centered */}
      <img
        src={after.src}
        srcSet={after.srcSet}
        sizes={after.sizes}
        loading="lazy"
        decoding="async"
        alt={afterLabel}
        className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
        draggable={false}
      />

      {/* BEFORE (left clipped) — inner image rendered at full slider width so both images align identically */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before.src}
          srcSet={before.srcSet}
          sizes={before.sizes}
          loading="lazy"
          decoding="async"
          alt={beforeLabel}
          className="absolute inset-0 h-full object-cover object-center"
          style={{ width: `${(100 / Math.max(pos, 0.001)) * 100}%`, maxWidth: "none" }}
          draggable={false}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 bg-black/70 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase text-white border border-white/10 pointer-events-none">
        {beforeLabel}
      </div>
      <div className="absolute top-4 right-4 bg-gold/95 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase text-black font-semibold pointer-events-none">
        {afterLabel}
      </div>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-gold pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-gold flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.6)]">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5">
            <path d="M9 18l-6-6 6-6M15 6l6 6-6 6" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const { t } = useLang();
  const [tab, setTab] = useState("cars");

  return (
    <section
      id="before-after"
      data-testid="before-after-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow={t.beforeAfter.eyebrow}
          title={t.beforeAfter.title}
          desc={t.beforeAfter.desc}
          align="center"
          testid="before-after-header"
        />

        <div className="mt-10 flex justify-center gap-3">
          {Object.keys(SETS).map((k) => (
            <button
              key={k}
              data-testid={`ba-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-6 py-2.5 text-xs tracking-[0.25em] uppercase border transition-colors ${
                tab === k
                  ? "border-gold text-gold"
                  : "border-white/15 text-zinc-400 hover:text-white"
              }`}
            >
              {t.beforeAfter.categories[k]}
            </button>
          ))}
        </div>

        <div className="mt-10 border border-white/10">
          <Slider
            before={SETS[tab].before}
            after={SETS[tab].after}
            beforeLabel={t.beforeAfter.beforeLabel}
            afterLabel={t.beforeAfter.afterLabel}
          />
        </div>
      </div>
    </section>
  );
};
